import React from 'react';
import { ArrowDown } from 'lucide-react';

const Hero = () => {
  const scrollToVentures = () => {
    document.getElementById('ventures')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white pt-24 sm:pt-20 px-4 overflow-hidden">
      {/* Animated Background Elements - Reduced on mobile */}
      <div className="absolute inset-0">
        {/* Gradient Orbs - Fewer and smaller on mobile */}
        <div className="absolute top-20 left-5 sm:left-10 w-48 h-48 sm:w-72 sm:h-72 bg-blue-500/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute top-40 right-10 sm:right-20 w-64 h-64 sm:w-96 sm:h-96 bg-purple-500/10 rounded-full blur-3xl animate-float-delayed"></div>
        <div className="absolute bottom-20 left-1/3 w-56 h-56 sm:w-80 sm:h-80 bg-slate-500/10 rounded-full blur-3xl animate-float-slow"></div>
        
        {/* Floating 3D Cubes - Hidden on very small screens */}
        <div className="hidden sm:block absolute top-1/4 left-1/4 animate-float-3d">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-slate-700/30 to-slate-800/30 backdrop-blur-sm border border-slate-600/30 rounded-lg transform rotate-12 shadow-2xl"></div>
        </div>
        <div className="hidden sm:block absolute top-1/3 right-1/4 animate-float-3d-delayed">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-slate-700/30 to-slate-800/30 backdrop-blur-sm border border-slate-600/30 rounded-lg transform -rotate-12 shadow-2xl"></div>
        </div>
        <div className="hidden md:block absolute bottom-1/4 right-1/3 animate-float-3d-slow">
          <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-slate-700/30 to-slate-800/30 backdrop-blur-sm border border-slate-600/30 rounded-lg transform rotate-45 shadow-2xl"></div>
        </div>
        
        {/* Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(148,163,184,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(148,163,184,0.05)_1px,transparent_1px)] bg-[size:32px_32px] sm:bg-[size:64px_64px]"></div>
      </div>

      {/* Wave Divider */}
      <div className="absolute bottom-0 left-0 right-0 pointer-events-none">
        <svg className="w-full h-16 sm:h-24 md:h-32" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
          <path d="M0,64 C240,96 480,96 720,64 C960,32 1200,32 1440,64 L1440,120 L0,120 Z" fill="white" fillOpacity="1"/>
          <path d="M0,80 C320,48 640,48 960,80 C1120,96 1280,96 1440,80 L1440,120 L0,120 Z" fill="white" fillOpacity="0.5"/>
        </svg>
      </div>
      
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-24 text-center z-10">
        {/* Glassmorphic Badge */}
        <div className="inline-block mb-4 sm:mb-6 px-4 sm:px-6 py-2 sm:py-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-xs sm:text-sm shadow-xl">
          <span className="bg-gradient-to-r from-slate-200 to-white bg-clip-text text-transparent font-semibold">Venture Studio & Business Solutions</span>
        </div>
        
        <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold mb-4 sm:mb-6 leading-tight px-2">
          Building intelligent systems
          <br />
          for <span className="italic text-slate-300 relative">
            <span className="relative z-10">real-world</span>
            <span className="absolute inset-0 blur-xl bg-slate-400/30"></span>
          </span> impact
        </h1>
        
        <p className="text-base sm:text-xl text-slate-300 mb-8 sm:mb-10 max-w-3xl mx-auto leading-relaxed px-2">
          OwlWorkLabs is a UK-based venture studio that identifies meaningful problems and builds scalable products at the intersection of technology, sustainability, and community.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-12 sm:mb-16 px-4">
          <button 
            className="group relative bg-white text-slate-900 hover:bg-slate-100 text-sm sm:text-base px-6 sm:px-8 py-3 rounded-lg font-semibold transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 w-full sm:w-auto"
            onClick={scrollToVentures}
            data-testid="hero-explore-ventures-btn"
          >
            <span className="relative z-10">Explore Our Ventures</span>
            <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-white to-slate-100 opacity-0 group-hover:opacity-100 transition-opacity"></div>
          </button>
          <a 
            href="https://tally.so/r/q4BLR9"
            target="_blank"
            rel="noopener noreferrer"
            className="relative bg-white/10 backdrop-blur-md border-2 border-white/30 text-white hover:bg-white/20 text-sm sm:text-base px-6 sm:px-8 py-3 rounded-lg font-semibold transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 w-full sm:w-auto text-center"
            data-testid="hero-partner-btn"
          >
            Partner With Us
          </a>
        </div>
        
        <div className="flex flex-col items-center gap-2 text-xs sm:text-sm text-slate-400 animate-bounce-slow">
          <span>Scroll to explore</span>
          <ArrowDown className="w-3 h-3 sm:w-4 sm:h-4" />
        </div>
      </div>
    </section>
  );
};

export default Hero;